import React, { useState, useEffect, useRef, useCallback } from 'react';
import 'primeflex/primeflex.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import {
  Widget,
  addResponseMessage,
  renderCustomComponent,
  addUserMessage,
  deleteMessages,
} from 'react-chat-widget';
import 'react-chat-widget/lib/styles.css';
import { Toast } from 'primereact/toast';
import { ModalInstall } from './ModalInstall';
import * as XLSX from 'xlsx';
import { DataTable } from 'primereact/datatable';
import { Column }    from 'primereact/column';
import { renderMarkdown } from '../utils/markdown';

// Tipo único que se envía al backend ahora que los chats están unificados.
// Si el servidor espera otra palabra clave, cámbiala aquí.
const CHAT_TYPE = 'general';

export const Chat = () => {
  const [password] = useState(localStorage.getItem('backend_password'));
  const [isProcessing, setIsProcessing] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [listening, setListening] = useState(false);
  const [animate, setAnimate] = useState(false);
  const toast = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  async function parseExcelToJson(blob) {
    const arrayBuffer = await blob.arrayBuffer();
    const workbook     = XLSX.read(arrayBuffer, { type: 'array' });
    const sheetName    = workbook.SheetNames[0];
    const worksheet    = workbook.Sheets[sheetName];

    // Obtén un array de arrays (cada fila es un array)
    const raw = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    const headers = raw[0] || [];
    const rows = raw.slice(1).map(row => {
      const obj = {};
      headers.forEach((h, i) => (obj[h] = row[i]));
      return obj;
    });

    return { headers, rows };
  }

  const toggleListening = async () => {
    if (listening) {
      mediaRecorderRef.current?.stop();
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioChunksRef.current = [];
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) audioChunksRef.current.push(e.data);
        };

        mediaRecorder.onstop = async () => {
          stream.getTracks().forEach(t => t.stop());
          setListening(false);
          setAnimate(false);

          const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const formData = new FormData();
          formData.append('audio', blob, 'audio.webm');
          formData.append('password', password);

          try {
            const res = await fetch('https://chatbot.ager-agro.es/api_garanango_powerbi/transcribe', {
              method: 'POST',
              body: formData,
            });
            const data = await res.json();
            if (data.transcript) {
              addUserMessage(data.transcript);
              handleNewUserMessage(data.transcript);
            } else {
              toast.current.show({ severity: 'warn', summary: 'Sin transcripción', detail: data.error || 'No se pudo transcribir el audio', life: 3000 });
            }
          } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al enviar el audio', life: 3000 });
          }
        };

        mediaRecorder.start();
        setListening(true);
      } catch (err) {
        toast.current.show({ severity: 'error', summary: 'Micrófono no disponible', detail: 'No se pudo acceder al micrófono', life: 3000 });
      }
    }
  };

  // Función para descargar el archivo
  const downloadFile = async (filename) => {
    try {
      const url_server_local = 'https://chatbot.ager-agro.es';
      const response = await fetch(url_server_local + '/api_garanango_powerbi/get_file', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password, filename })
      });
      if (!response.ok) {
        throw new Error('Error al descargar el archivo');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Error al descargar el archivo', error);
      toast.current.show({
        severity: 'error',
        summary: 'Descarga fallida',
        detail: 'No se pudo descargar el archivo',
        life: 3000
      });
    }
  };

  // Asignamos la función downloadFile al objeto window para que sea accesible desde el onclick inline
  useEffect(() => {
    window.downloadFile = downloadFile;
  }, [downloadFile]);

  const checkConnection = async () => {
    try {
      await fetch('https://www.google.com?no-sw=true', { mode: 'no-cors' });
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No tienes conexión a Internet',
        life: 3000
      });
    }
  };

  // useEffect para borrar mensajes previos, chequear conexión y dar la bienvenida
  useEffect(() => {
    deleteMessages();
    checkConnection();
    addResponseMessage('¡Bienvenido! ¿En qué puedo ayudarte?');
  }, []);

  const handleNewUserMessage = useCallback(async (question) => {
    // Evitar que el usuario envíe otra pregunta mientras se procesa
    if (isProcessing) {
      toast.current.show({
        severity: 'error',
        summary: 'Procesando',
        detail: 'Por favor, espera a que se termine de procesar la respuesta.',
        life: 3000
      });
      // Borrar el último mensaje enviado por el usuario
      deleteMessages(1);

      // Opcional: Devolver el texto al input
      const inputElement = document.querySelector('.rcw-input');
      if (inputElement) {
        setTimeout(() => {
          inputElement.innerText = question;
        }, 50);
      }
      return;
    }

    setIsProcessing(true);
    try {
      const messagesContainer = document.querySelector('.rcw-messages-container');
      let loadingDiv;
      if (messagesContainer) {
        loadingDiv = document.createElement('div');
        loadingDiv.className = 'loading-container';

        const loadingChatDiv = document.createElement('div');
        loadingChatDiv.className = 'loading-chat';

        loadingDiv.appendChild(loadingChatDiv);
        messagesContainer.appendChild(loadingDiv);
      }

      const inputElement = document.querySelector('.rcw-send');
      if (inputElement) {
        inputElement.focus();
        inputElement.blur();
      }

      const url_server_local = 'https://chatbot.ager-agro.es';
      const response = await fetch(url_server_local + '/api_garanango_powerbi/ask', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ question, type: CHAT_TYPE, thread_id: threadId, password: password }),
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error('Error en la respuesta de la API');
      } else if (loadingDiv) {
        messagesContainer.removeChild(loadingDiv);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let completeResponse = '';
      let scrollCount = 0;

      // Agregar un mensaje inicial vacío para actualizarlo con la respuesta en stream
      addResponseMessage('');

      // Leer el stream de la respuesta de forma progresiva
      await new Promise((resolve) => {
        setTimeout(() => {
          const messages = document.querySelectorAll('.rcw-message-text');
          const lastMessageElement = messages[messages.length - 1];
          (async () => {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              const chunk = decoder.decode(value, { stream: true });
              completeResponse += chunk;

              // Si existe "historial:", cortamos la respuesta antes de eso
              const markerIndex = completeResponse.indexOf('\nhistorial: ');
              let displayResponse = completeResponse;
              if (markerIndex !== -1) {
                displayResponse = completeResponse.substring(0, markerIndex);
              }

              const formattedResponse = renderMarkdown(displayResponse);

              if (lastMessageElement) {
                lastMessageElement.innerHTML = formattedResponse;
                if (scrollCount < 100) {
                  const messagesContainer = document.querySelector('.rcw-messages-container');
                  if (messagesContainer) {
                    messagesContainer.scrollTop = messagesContainer.scrollHeight;
                    scrollCount++;
                  }
                }
              }
            }
            resolve();
          })();
        }, 100);
      });

      setThreadId(response.headers.get('X-Thread-Id'));

      const messages = document.querySelectorAll('.rcw-message-text');
      const lastMessageEl = messages[messages.length - 1];

      const html = lastMessageEl.innerHTML;
      const downloadLinkMatch = html.match(/onclick="downloadFile\('([^']+)'\)"/);

      if (downloadLinkMatch) {
        const filename = downloadLinkMatch[1];

        // Descarga el blob
        const blobResp = await fetch(url_server_local + '/api_garanango_powerbi/get_file', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password, filename })
        });
        const blob = await blobResp.blob();

        // Parsear a JSON
        const { headers, rows } = await parseExcelToJson(blob);

        // Renderizar DataTable
        renderCustomComponent(
          () => (
            <div style={{ overflowX: 'auto', maxWidth: '100%', marginTop: '0.5rem', borderRadius: '10px', backgroundColor: '#3d0090' }}>
              <DataTable
                value={rows}
                paginator
                rows={10}
                stripedRows
                style={{ background: 'white', borderRadius: '6px' }}
              >
                {headers.map((field) => (
                  <Column
                    key={field}
                    field={field}
                    header={field}
                    sortable
                    filter
                    headerStyle={{ backgroundColor: '#3d0090', color: '#fff' }}
                  />
                ))}
              </DataTable>
            </div>
          ),
          { align: 'left' }
        );
      }
    } catch (error) {
      console.error('Error al obtener respuesta:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [threadId, password, isProcessing]);

  const handleClickMicro = () => {
    setAnimate(true);
    toggleListening();
  };

  return (
    <>
      <ModalInstall />
      <div className="body-div flex flex-column align-items-center">
        <Toast ref={toast} />
        <div
          onClick={handleClickMicro}
          style={{
            position: 'absolute',
            bottom: '9px',
            right: '72px',
            zIndex: 9999,
            padding: '16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <i
            className={`
              pi pi-microphone
              ${listening ? 'listening' : ''}
              ${animate   ? 'pulse'     : ''}
            `}
            style={{
              fontSize: '1.5rem',
              color: listening ? 'red' : '#48A6FF',
              pointerEvents: 'none',
            }}
          />
        </div>
        <Widget
          handleNewUserMessage={handleNewUserMessage}
          subtitle={null}
          emojis={true}
          title={'FitoGes'}
          showTimeStamp={false}
          showCloseButton={false}
          senderPlaceHolder={'Escribe tu mensaje...'}
          style={{ maxWidth: '100%', margin: '0 auto' }}
        />
      </div>
    </>
  );
};
